/**
 * Pure countdown math for the operator past-due warning banner (roadmap #9).
 *
 * Extracted from the banner component so the estimate is a single,
 * unit-testable source of truth with NO React/Firestore dependency.
 *
 * WHY THIS IS AN ESTIMATE (and the banner copy must hedge):
 * the server flips a tenant `past_due → suspended` in `decideTenantLifecycle`
 * (packages/shared) once the OLDEST unpaid invoice is `suspendAfterDays`
 * overdue — i.e. at `oldestUnpaidDueMs + suspendAfterDays days`. That
 * transition only fires on the daily `lifecycleDaily` sweep, which runs at
 * most once a day, so the real suspension can lag this threshold by up to
 * ~24h. This function reports the THRESHOLD, never a guarantee — the UI states
 * it as "about N days" / "on or around {date}".
 */

const DAY_MS = 86_400_000;

export interface SuspensionCountdown {
  /** Epoch ms of the estimated suspension threshold (oldest due + window). */
  readonly suspendDateMs: number;
  /**
   * Whole days remaining until the threshold, rounded UP so the countdown
   * never understates the time an operator has. Can be `<= 0` once the
   * threshold has been reached/passed (suspension pending the next sweep).
   */
  readonly daysLeft: number;
  /**
   * True once the threshold is reached or passed (`daysLeft <= 0`) — the UI
   * shows an "imminent / very soon" variant instead of a zero/negative count.
   */
  readonly imminent: boolean;
}

/**
 * Honest estimate of when a `past_due` tenant will be suspended.
 *
 * @param oldestUnpaidDueMs  epoch ms of the OLDEST unpaid invoice's dueDate
 * @param nowMs              current epoch ms (live clock, not a stored value)
 * @param suspendAfterDays   days overdue before suspension — pass
 *                           `DEFAULT_LIFECYCLE_CONFIG.suspendAfterDays`, never
 *                           a hard-coded literal, so it tracks the shared policy
 */
export function computeDaysUntilSuspension(
  oldestUnpaidDueMs: number,
  nowMs: number,
  suspendAfterDays: number,
): SuspensionCountdown {
  const suspendDateMs = oldestUnpaidDueMs + suspendAfterDays * DAY_MS;
  const daysLeft = Math.ceil((suspendDateMs - nowMs) / DAY_MS);
  return { suspendDateMs, daysLeft, imminent: daysLeft <= 0 };
}

/** The subset of `useI18n().t` this helper needs (key + interpolation vars). */
export type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;

/**
 * Assemble the human countdown line from a countdown + a translate function.
 *
 * Extracted from the component specifically so the i18n VAR NAMES are unit-
 * tested against the real catalog: a var/placeholder mismatch (e.g. passing
 * `{ n }` to a `"{{days}} days"` string) silently renders a literal "{{days}}"
 * to the operator, which a React-free render harness can't catch here. Returns
 * null when there's no countdown (the caller then hides the line).
 */
export function assembleCountdownLine(
  countdown: SuspensionCountdown | null,
  t: TranslateFn,
  formatDate: (ms: number) => string,
): string | null {
  if (!countdown) return null;
  if (countdown.imminent) return t('dashboard.pastDue.countdownImminent');
  const daysLabel =
    countdown.daysLeft === 1
      ? t('dashboard.pastDue.daysOne')
      : t('dashboard.pastDue.daysOther', { days: countdown.daysLeft });
  return t('dashboard.pastDue.countdown', {
    days: daysLabel,
    date: formatDate(countdown.suspendDateMs),
  });
}
